import { useTranslation, untranslated } from "@i18n";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { AccordionDetails, AccordionSummary } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconComponent from "@CubeComponents/icons";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { fetchCostDatabaseData } from "@Actions/portalSetting";
import { useParams } from "react-router-dom";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import EstimateCostDatabaseItems from "./EstimateCostDatabaseItemsGridNew";
import {
  changeCostDatabaseVersionAttachedToEstimate,
  checkIfCostDatabaseVersionIsLatest,
  isEstimateRequiredPermission,
  itemListSources,
  updateEstimateAttachedCostDatabase,
  updateEstimateData,
} from "@Actions/estimation";
import Accordian from "@CubeComponents/html/accordion/materialUiAccordian";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import _ from "lodash";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { getEstimateData } from "@Actions/EstimateList";
import { folderPermissions } from "@Helpers/constants";
import { errorCodes } from "@Helpers/errorCodes";
import type { RootState } from "src/reducers";
import type { EstimateData as EstimateDataState, EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY } = folderPermissions();

type CurrencyData = EstimateCurrency;

type CostDatabaseRecord = {
  /** CostDatabases `_id` — `objectIdString` in costDatabase.schema.ts. */
  _id: ObjectIdString;
  versionData?: { name?: string };
};

type CostDatabaseDetails = {
  _id?: ObjectIdString | null;
  costIndex?: number;
  costDatabaseVersionId?: ObjectIdString;
  openConfirmModal?: boolean;
};

type EstimateDataDetail = EstimateDataState & {
  costDatabase?: {
    /** `costDatabase.id` / `.costDatabaseVersionId` are stored as ObjectId (estimates.collection.ts). */
    id?: ObjectIdString;
    name?: string;
    costDatabaseVersionId?: ObjectIdString;
    costDatabaseVersion?: number;
  };
  costIndex?: number;
  userRole?: number;
};

const EstimateCostDatabaseConfigration = ({ currencyData }: { currencyData: CurrencyData }) => {
  const { t } = useTranslation();
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataDetail;
  const costDatabaseList = useSelector((state: RootState) => state.getCostDatabaseData) as CostDatabaseRecord[];
  const dispatch = useDispatch();
  // Entry point: route params are outside the type system; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString; projectId: ObjectIdString }>();
  const [costDatabaseDetails, setCostDatabaseDetails] = useState<CostDatabaseDetails>({});
  const [currentCostDatabaseDetails, setCurrentCostDatabaseDetails] = useState<CostDatabaseDetails>({
    _id: estimateData.costDatabase?.id,
    costIndex: _.get(estimateData, "costIndex") as number | undefined,
  });
  const [buttonLoading, setButtonLoading] = useState(false);
  const [enableUpgradeCostDatabaseVersionBtn, setEnableUpgradeCostDatabaseVersionBtn] = useState(false);
  const [showConfirmUpgradeCDModal, setShowConfirmUpgradeCDModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [accordianState, setAccordianState] = useState({ costDatabase: true, items: true });
  const costDatabaseRef = useRef<HTMLDivElement | null>(null);
  const estimateVersionId = params.versionId;

  const actionPermissionAllowed = isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);

  const checkCostDatabaseVersionIsLatest = useCallback(async () => {
    if (!estimateData._id) return;
    const response = await checkIfCostDatabaseVersionIsLatest({
      estimateId: estimateData._id,
    });
    setEnableUpgradeCostDatabaseVersionBtn(!response.data.status);
  }, [estimateData._id]);

  const estimateCostDatabaseIdDep = _.get(estimateData, "costDatabase.id");
  const estimateCostDatabaseVersionIdDep = _.get(estimateData, "costDatabase.costDatabaseVersionId");
  const estimateCostIndexDep = _.get(estimateData, "costIndex");
  useEffect(() => {
    if (estimateData._id) {
      setCostDatabaseDetails({
        _id: estimateData.costDatabase?.id,
        costIndex: estimateData.costIndex,
        costDatabaseVersionId: estimateData.costDatabase?.costDatabaseVersionId,
      });
      setCurrentCostDatabaseDetails({
        _id: estimateData.costDatabase?.id,
        costIndex: estimateData.costIndex,
      });
      if (estimateData.costDatabase?.id) {
        checkCostDatabaseVersionIsLatest();
      }
    }
  }, [
    estimateCostDatabaseIdDep,
    estimateCostDatabaseVersionIdDep,
    estimateCostIndexDep,
    estimateData._id,
    estimateData.costDatabase?.costDatabaseVersionId,
    estimateData.costDatabase?.id,
    estimateData.costIndex,
    checkCostDatabaseVersionIsLatest,
  ]);

  useEffect(() => {
    dispatch(fetchCostDatabaseData({ value: { projectId: params.projectId } }));
  }, [params.projectId]);

  const attachedCostDatabaseChangeHandler = (e: {
    value?: ObjectIdString | null;
    itemData?: CostDatabaseRecord | null;
  }) => {
    setCurrentCostDatabaseDetails((prev) => ({
      ...prev,
      _id: e.value,
      openConfirmModal: true,
    }));
    // MUI Autocomplete closes popup automatically on selection
  };

  const removeCostDatabaseClickHandler = () => {
    setCurrentCostDatabaseDetails((prev) => ({
      ...prev,
      _id: null,
      openConfirmModal: true,
    }));
  };

  const costIndexOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentCostDatabaseDetails((prev) => ({ ...prev, costIndex: Number(event.target.value) }));
  };

  const costIndexChangeHandler = (e?: { stopPropagation?: () => void }) => {
    if (!currentCostDatabaseDetails.costIndex || currentCostDatabaseDetails.costIndex <= 0) {
      e?.stopPropagation?.();
      dispatch(
        showToastNotification({
          content: t("Please enter a positive value for Cost Index."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      setCurrentCostDatabaseDetails(costDatabaseDetails);
      return;
    }
    if (currentCostDatabaseDetails.costIndex !== costDatabaseDetails.costIndex) {
      setCurrentCostDatabaseDetails((prev) => ({
        ...prev,
        openConfirmModal: true,
      }));
    }
  };

  const keyChangeHandler = (e: { keyCode: number }) => {
    if (e.keyCode === 13) {
      costIndexChangeHandler();
    }
  };

  const getCostDatabaseList = useCallback(
    () => (
      <AutoComplete
        ref={costDatabaseRef}
        width="25rem"
        value={costDatabaseList.find((opt: CostDatabaseRecord) => opt._id === currentCostDatabaseDetails._id) || null}
        options={costDatabaseList}
        valueField="_id"
        getOptionLabel={(option: CostDatabaseRecord) => option?.versionData?.name || ""}
        onChange={(_event: React.SyntheticEvent, newValue: CostDatabaseRecord | null) =>
          attachedCostDatabaseChangeHandler({ value: newValue?._id, itemData: newValue })
        }
        disabled={!actionPermissionAllowed || buttonLoading || !!estimateVersionId}
        renderInput={(params: object) => <TextFieldComponent {...params} placeholder={t("Select Cost Database")} />}
      />
    ),
    [currentCostDatabaseDetails._id, costDatabaseList, actionPermissionAllowed, buttonLoading, estimateVersionId],
  );

  const upgradeToLatestCostDatabaseVersion = async (forceDeletedItemToManual?: boolean) => {
    if (!actionPermissionAllowed) {
      dispatch(
        showToastNotification({
          content: t("You Don't Have Required Permission to Perform this Action"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      if (!buttonLoading && params.estimateId) {
        setButtonLoading(true);
        const response = await changeCostDatabaseVersionAttachedToEstimate({
          _id: params.estimateId,
          forceDeletedItemToManual,
        });

        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("The Cost Database version has been upgraded successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          dispatch(getEstimateData({ estimateId: params.estimateId! }));
          checkCostDatabaseVersionIsLatest();
        } else {
          if (response.data.errorCode === 9200) {
            setShowConfirmUpgradeCDModal(true);
          } else {
            dispatch(
              showToastNotification({
                content: t("The Cost Database version could not be upgraded"),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        }

        setButtonLoading(false);
      }
    }
  };

  const upgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const costDatabaseTemplate = () => (
    <div className="flex gap-10">
      <div className="px-2.5 py-1.5 text-sm font-normal flex w-100 justify-between items-center rounded border border-button_light">
        <span>{estimateData.costDatabase?.name ?? ""}</span>
        {actionPermissionAllowed && !buttonLoading && !estimateVersionId && (
          <IconComponent
            name="close"
            className="cursor-pointer"
            onClick={removeCostDatabaseClickHandler}
            width="1rem"
            height="1rem"
          />
        )}
      </div>
      {enableUpgradeCostDatabaseVersionBtn && actionPermissionAllowed && !estimateVersionId && (
        <div
          className="flex gap-2 cursor-pointer items-center text-sm text-system_colour_43 font-semibold py-1.5 px-2.5 rounded-2xl bg-dark_neutral"
          onClick={upgradeClick}
        >
          <span>{`V${estimateData.costDatabase?.costDatabaseVersion ?? ""}`}</span>
          <IconComponent name="UpdateWhite" width="1.25rem" height="1.25rem" />
        </div>
      )}
    </div>
  );

  const costDatabaseContent = () => (
    <div className="flex flex-col gap-5">
      <div className="flex gap-6">
        <div className="flex w-32 gap-1 items-center">
          <span>{t("Cost Database")}</span>

          <IconComponent
            name="details"
            position="bottom"
            toolTipContent={t(
              "Ensure that the Cost Database that you want to use for this estimate is in the same currency as defined in the settings of this Project. Also, atleast one version of the Cost Database must be published for it to be visible in this list.",
            )}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
        {costDatabaseDetails._id ? costDatabaseTemplate() : getCostDatabaseList()}
      </div>
      <div className="flex gap-6">
        <div className="flex">
          <div className="flex w-32 gap-1 items-center">
            <span>{t("Cost Index")}</span>

            <IconComponent
              name="details"
              position="bottom"
              toolTipContent={t(
                "This value will be used to multiply the rate of every item from a cost database. It will not affect items that are added directly to the estimate.",
              )}
              width="1.25rem"
              height="1.25rem"
            />
          </div>
        </div>
        <TextFieldComponent
          width="25rem"
          type="number"
          placeholder={t("Enter Value")}
          value={currentCostDatabaseDetails.costIndex?.toString() ?? ""}
          onChange={costIndexOnChange}
          disabled={!currentCostDatabaseDetails._id || !actionPermissionAllowed || buttonLoading || !!estimateVersionId}
          onBlur={costIndexChangeHandler}
          onKeyDown={keyChangeHandler}
        />
      </div>
    </div>
  );

  const costDatabaseItemsContent = () => (
    <EstimateCostDatabaseItems
      isStaticData={true}
      costDatabaseId={costDatabaseDetails._id ?? undefined}
      costDatabaseVersionId={costDatabaseDetails.costDatabaseVersionId}
      source={itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW}
      currencyData={currencyData}
    />
  );

  const renderCostDatabaseDetails = () => (
    <div className="flex w-full border-0 border-b border-button_light">
      <Accordian
        className="w-full rounded-none border-0 shadow-none"
        expanded={accordianState.costDatabase}
        onChange={() =>
          setAccordianState((prev) => ({
            ...prev,
            costDatabase: !prev.costDatabase,
          }))
        }
      >
        <AccordionSummary className="px-5 py-3" expandIcon={<ExpandMoreIcon />}>
          <div className="w-full text-lg font-semibold">{t("Cost Database")}</div>
        </AccordionSummary>
        <AccordionDetails className="px-6 pb-4 pt-2 border-0">{costDatabaseContent()}</AccordionDetails>
      </Accordian>
    </div>
  );

  const costDatabaseItemsDetails = () => (
    <div className="flex h-full overflow-auto w-full">
      <Accordian
        className="w-full rounded-none border-0 shadow-none flex flex-col h-full overflow-auto"
        expanded={accordianState.items}
        onChange={() =>
          setAccordianState((prev) => ({
            ...prev,
            items: !prev.items,
          }))
        }
        sx={{
          "& .MuiCollapse-root": {
            height: "100%",
            overflow: "auto",
            display: "flex",
          },
        }}
      >
        <AccordionSummary className="px-5 py-5" expandIcon={<ExpandMoreIcon />}>
          <div className="w-full text-lg font-semibold">{t("Linked Cost Database Preview")}</div>
        </AccordionSummary>
        <AccordionDetails className="px-6 py-0 flex overflow-auto h-full">
          {costDatabaseItemsContent()}
        </AccordionDetails>
      </Accordian>
    </div>
  );

  async function handleUpgradeCDVersion() {
    await upgradeToLatestCostDatabaseVersion(true);
    setShowConfirmUpgradeCDModal(false);
  }

  const handleConfirmClick = async () => {
    if (!params.estimateId) return;
    setButtonLoading(true);
    let response;
    if (costDatabaseDetails._id != currentCostDatabaseDetails._id) {
      response = await updateEstimateAttachedCostDatabase({
        estimateId: params.estimateId,
        costDatabaseId: currentCostDatabaseDetails._id ?? null,
      });
    } else {
      response = await updateEstimateData({
        estimateId: params.estimateId,
        costIndex: currentCostDatabaseDetails.costIndex,
      });
    }
    if (response.data.status) {
      dispatch(getEstimateData({ estimateId: params.estimateId! }));
      dispatch(
        showToastNotification({
          content:
            costDatabaseDetails._id !== currentCostDatabaseDetails._id
              ? t("Cost Database updated successfully!")
              : t("Cost Index updated successfully!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      let errorMessage = response.data.errorMessage || SOMETHING_WENT_WRONG;
      // @ts-expect-error -- `errorCode` is read after narrowing to the success arm `{ status: true }`, which does not declare it. SCHEMA GAP: the response schema's success arm omits the error fields this branch reads.
      if (response.data.errorCode == errorCodes.ESTIMATE_COST_DATABASE_UPGRADE_TAKEOFF_WIZARD_ERROR) {
        errorMessage = t(
          "Cost Database cannot be removed because the Takeoff Wizard has been used. Please undo the Takeoff Wizard to proceed.",
        );
      }
      dispatch(showToastNotification({ content: untranslated(errorMessage), type: TOAST_TYPE_ERROR }));
      setCurrentCostDatabaseDetails(costDatabaseDetails);
    }
    setCurrentCostDatabaseDetails((prev) => ({ ...prev, openConfirmModal: false }));
    setButtonLoading(false);
  };

  const handleCloseConfirmModal = () => {
    if (!buttonLoading) {
      if (showConfirmUpgradeCDModal) {
        setShowConfirmUpgradeCDModal(false);
      } else if (showUpgradeModal) {
        setShowUpgradeModal(false);
      } else {
        setCurrentCostDatabaseDetails(costDatabaseDetails);
      }
    }
  };

  const handleSubmitModal = async () => {
    if (showConfirmUpgradeCDModal) {
      await handleUpgradeCDVersion();
    } else if (showUpgradeModal) {
      await upgradeToLatestCostDatabaseVersion();
      setShowUpgradeModal(false);
    } else {
      await handleConfirmClick();
    }
  };

  const getHeaderAndContentAndWarningText = () => {
    let header, content, warning;
    if (showConfirmUpgradeCDModal || showUpgradeModal) {
      header = "Upgrade Cost Database";
      content = "Are you sure you want to upgrade the Cost Database to the latest version?";
      if (showConfirmUpgradeCDModal) {
        warning =
          "Some item(s) were deleted in the Cost Database and hence to upgrade to the latest version in this estimate, those items need to be converted to Manual items.";
      }
    } else {
      if (currentCostDatabaseDetails._id != costDatabaseDetails._id) {
        if (currentCostDatabaseDetails._id == null) {
          header = "Remove Cost Database";
          content = "Are you sure you want to remove Cost Database?";
          warning =
            "Removing the Cost Database will ‘Change type’ for all the items added from the Cost Database and convert them into manual items.";
        } else {
          header = "Associate Cost Database";
          content = "Are you sure you want to associate Cost Database?";
        }
      } else {
        header = "Update Cost index";
        content = "Are you sure you want to Update  Cost Index?";
        warning =
          "Updating the cost index will impact all previously imported items from the Cost Database (if any) and all future imported items.";
      }
    }

    return { header, content, warning };
  };

  const modalHeader = () => {
    const { header } = getHeaderAndContentAndWarningText();
    return (
      <div className="flex justify-between w-full items-center text-2xl font-semibold">
        <span className="">{header}</span>
      </div>
    );
  };

  const modalFooter = () => (
    <div className="flex gap-4">
      <ButtonComponent onClick={handleSubmitModal} buttonType={BUTTON_TYPES.GRADIENT_BUTTON} loading={buttonLoading}>
        {t("Confirm")}
      </ButtonComponent>
      <ButtonComponent
        onClick={handleCloseConfirmModal}
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        loading={buttonLoading}
      >
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  const modalContent = () => {
    const { content, warning } = getHeaderAndContentAndWarningText();
    return (
      <div className="text-sm font-normal flex flex-col gap-6">
        <span>{content}</span>
        {warning && <div className="p-4 rounded-lg bg-banner_color_yellow bg-opacity-40">{warning}</div>}
      </div>
    );
  };

  const CDRelatedDetailsConfirmationModal = () => {
    return (
      (currentCostDatabaseDetails.openConfirmModal || showConfirmUpgradeCDModal || showUpgradeModal) && (
        <ModalStateDialogComponent
          headerTitle={modalHeader()}
          modelContent={modalContent()}
          footerContent={modalFooter()}
          isVisible={currentCostDatabaseDetails.openConfirmModal || showConfirmUpgradeCDModal || showUpgradeModal}
          width="28.125rem"
          onCancel={handleCloseConfirmModal}
        />
      )
    );
  };

  return (
    <div className="flex flex-col w-full h-full overflow-auto">
      {CDRelatedDetailsConfirmationModal()}
      {renderCostDatabaseDetails()}
      {costDatabaseDetails._id && costDatabaseItemsDetails()}
    </div>
  );
};

export default EstimateCostDatabaseConfigration;
